import {Loader} from "@/components/placeholder/Loader.tsx";
import {timezoneAbbr} from "@/lib/format.tsx";
import type {ApexOptions} from "apexcharts";
import {formatInTimeZone} from "date-fns-tz";
import {useEffect, useRef, useState} from "react";
import Chart from "react-apexcharts";
import styles from "./MetricChart.module.scss";

export type MetricTimeRange = "day" | "week" | "month";

export interface MetricChartProps {
  data: [number, number | null][];
  timezone?: string;
  loading?: boolean;
}

export const MetricChart = ({data, timezone, loading}: MetricChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const themeRoot = el.closest("[data-theme]");
    if (!themeRoot) return;

    const observer = new MutationObserver(() => {
      setIsDark(themeRoot.getAttribute("data-theme") === "dark");
    });
    setIsDark(themeRoot.getAttribute("data-theme") === "dark");
    observer.observe(themeRoot, {attributes: true, attributeFilter: ["data-theme"]});
    return () => observer.disconnect();
  }, [loading]);

  if (loading) {
    return (
      <div className={styles.emptyChart}>
        <Loader />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div ref={containerRef} className={styles.emptyChart}>
        No data
      </div>
    );
  }

  const lineColor = isDark ? styles.chartLineColorDark : styles.chartLineColor;
  const fillColor = isDark ? styles.chartFillColorDark : styles.chartFillColor;
  const axisColor = isDark ? styles.chartAxisColorDark : styles.chartAxisColor;
  const gridColor = isDark ? styles.chartGridColorDark : styles.chartGridColor;
  const {chartAxisFontSize} = styles;
  // Convert seconds to milliseconds for y-axis display
  const series = [
    {
      name: "Response Time",
      data: data.map(([ts, val]) => ({
        x: ts,
        // null carries through to ApexCharts as a gap in the line.
        y: val === null ? null : parseFloat((val * 1000).toFixed(2)),
      })),
    },
  ];

  const tz = timezone ?? "UTC";

  // timezoneAbbr (moment-based) yields the proper abbreviation per timestamp (e.g. EDT, EEST),
  // matching legacy; date-fns/Intl only produce raw offsets like "GMT+3" for many zones.
  const formatWithTz = (ts: number) => `${formatInTimeZone(ts, tz, "MMM d, yyyy HH:mm:ss")} ${timezoneAbbr(tz, ts)}`;

  const formatAxisLabel = (_value: string, timestamp?: number) => {
    if (timestamp === undefined) return "";
    return formatWithTz(timestamp);
  };

  const formatTooltipX = (timestamp?: number) => {
    if (timestamp === undefined) return "";
    return formatWithTz(timestamp);
  };

  const options: ApexOptions = {
    chart: {
      type: "area",
      toolbar: {show: false},
      zoom: {enabled: false},
      animations: {enabled: false},
      background: "transparent",
      fontFamily: "inherit",
    },
    stroke: {
      curve: "smooth",
      width: 1.5,
      colors: [lineColor],
    },
    fill: {
      type: "solid",
      colors: [fillColor],
      opacity: 1,
    },
    colors: [lineColor],
    dataLabels: {enabled: false},
    tooltip: {
      enabled: true,
      theme: isDark ? "dark" : "light",
      x: {formatter: formatTooltipX},
      y: {
        formatter: (val: number) => `${val.toFixed(0)}ms`,
      },
    },
    xaxis: {
      type: "datetime",
      axisBorder: {show: false},
      axisTicks: {show: false},
      labels: {
        style: {
          fontSize: chartAxisFontSize,
          colors: axisColor,
        },
        formatter: formatAxisLabel,
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => `${val.toFixed(0)}ms`,
        style: {
          fontSize: chartAxisFontSize,
          colors: axisColor,
        },
      },
    },
    grid: {
      borderColor: gridColor,
      strokeDashArray: 0,
      xaxis: {lines: {show: true}},
      yaxis: {lines: {show: true}},
      padding: {top: 8, right: 8, bottom: 0, left: 8},
    },
    markers: {size: 0},
  };

  return (
    <div ref={containerRef} style={{height: "100%"}}>
      <Chart type="area" width="100%" height="100%" series={series} options={options} />
    </div>
  );
};
